import { createClient, type SupabaseClient } from "@supabase/supabase-js";

import { E2E_SIGNED_OUT_KEY } from "./e2eSignedOut";

// Browser-Supabase-Client.
// Persistiert die Session automatisch in localStorage (Supabase-Default),
// refresht den Access-Token rechtzeitig selber.
//
// Session 30 / H1: im E2E-Modus (`VITE_E2E_MODE=1`) verwenden wir einen Stub,
// damit Playwright ohne echte Supabase-Verbindung rendert. Der Stub liefert
// eine synthetische Session (reicht fuer AuthContext.getSession) und einen
// No-Op-Subscriber. Alle `/api/*`-Calls werden in Playwright via `page.route`
// abgefangen. Nur fuer Build-Time-Stubbing — Production-Bundles sehen diesen
// Pfad nicht, weil `VITE_E2E_MODE` in der Produktion nie gesetzt ist.

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const e2eMode = import.meta.env.VITE_E2E_MODE === "1";

function createE2EStub(): SupabaseClient {
  const nowIso = new Date().toISOString();
  const fakeSession = {
    access_token: "e2e-access-token",
    refresh_token: "e2e-refresh-token",
    expires_at: Math.floor(Date.now() / 1000) + 3600,
    expires_in: 3600,
    token_type: "bearer",
    user: {
      id: "00000000-0000-4000-8000-0000000000e2",
      email: "e2e@bergler-energie.ch",
      app_metadata: {},
      user_metadata: {},
      aud: "authenticated",
      created_at: nowIso,
    },
  };
  // CI-2: ein Test kann «abgemeldet» verlangen (die Anmeldeseite laesst sich
  // sonst gar nicht pruefen — ein Angemeldeter wird von ihr sofort
  // weitergeleitet). Gelesen bei jedem Abruf, gesetzt per `addInitScript`.
  const session = () =>
    window.localStorage.getItem(E2E_SIGNED_OUT_KEY) === "1" ? null : fakeSession;
  const authStub = {
    getSession: () =>
      Promise.resolve({ data: { session: session() }, error: null }),
    onAuthStateChange: () => ({
      data: { subscription: { unsubscribe: () => undefined } },
    }),
    signOut: () => Promise.resolve({ error: null }),
    signInWithOAuth: () => Promise.resolve({ data: null, error: null }),
  };
  // Der Stub deckt nur die im AuthContext genutzten Methoden ab. Fuer TS
  // reicht ein Cast ueber `unknown` — der Stub landet nie in Production.
  return { auth: authStub } as unknown as SupabaseClient;
}

function createRealClient(): SupabaseClient {
  if (!url || !anonKey) {
    throw new Error(
      "VITE_SUPABASE_URL oder VITE_SUPABASE_ANON_KEY fehlt in der Vite-Env.",
    );
  }
  return createClient(url, anonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: "pkce",
    },
  }) as unknown as SupabaseClient;
}

export const supabase: SupabaseClient = e2eMode
  ? createE2EStub()
  : createRealClient();
